import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { 
  Mail, 
  Send,
  MessageCircle,
  AlertTriangle,
  Clock,
  User
} from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useEmailAnalytics } from '@/hooks/useEmailAnalytics'
import { EmailEventDetailModal } from '@/components/shared/EmailEventDetailModal'
import { smartleadAPI } from '@/lib/smartlead-api'
import { DEMO_SENT_EVENTS, DEMO_REPLY_EVENTS, DEMO_BOUNCE_EVENTS } from '@/data/demoLiveEvents'

interface EmailEvent {
  id: string
  event_type: 'EMAIL_SENT' | 'EMAIL_REPLY' | 'EMAIL_BOUNCE'
  from_email: string
  to_email: string
  to_name?: string
  campaign_name?: string
  campaign_id?: number
  client_id?: number
  subject?: string
  reply_body?: string
  created_at: string
  category?: string
  bounce_message?: any
}

export function EmailsSentLiveStream() {
  const [sentEvents, setSentEvents] = useState<EmailEvent[]>([])
  const [replyEvents, setReplyEvents] = useState<EmailEvent[]>([])
  const [bounceEvents, setBounceEvents] = useState<EmailEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedEvent, setSelectedEvent] = useState<any>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { data: emailAnalytics } = useEmailAnalytics()

  // Use analytics data for today's stats to ensure consistency
  const todayStats = {
    total_sent: emailAnalytics?.today || 0,
    total_replies: emailAnalytics?.today_replies || 0,
    total_bounces: emailAnalytics?.today_bounces || 0
  }

  // Fetch today's events using UTC date boundaries to match analytics
  useEffect(() => {
    const fetchTodayEvents = async () => {
      try {
        // Check if Smartlead is configured - if not, use mock data
        if (!smartleadAPI.isConfigured()) {
          console.log('🔍 [EmailsSentLiveStream] Smartlead API not configured, using mock data for demo mode...');
          
          // Convert demo events to EmailEvent format
          const demoSentEvents: EmailEvent[] = DEMO_SENT_EVENTS.map(event => ({
            id: event.id,
            event_type: 'EMAIL_SENT' as const,
            from_email: event.senderEmail,
            to_email: event.leadEmail,
            to_name: event.leadName,
            campaign_name: event.campaignName,
            campaign_id: Math.floor(Math.random() * 10) + 1,
            client_id: Math.floor(Math.random() * 8) + 1,
            subject: event.subject,
            created_at: event.timestamp
          }));

          const demoReplyEvents: EmailEvent[] = DEMO_REPLY_EVENTS.map(event => ({
            id: event.id,
            event_type: 'EMAIL_REPLY' as const,
            from_email: event.leadEmail,
            to_email: event.senderEmail,
            to_name: event.leadName,
            campaign_name: event.campaignName,
            campaign_id: Math.floor(Math.random() * 10) + 1,
            client_id: Math.floor(Math.random() * 8) + 1,
            subject: event.subject,
            reply_body: 'Thanks for reaching out! This sounds interesting. Can we schedule a call?',
            created_at: event.timestamp
          }));

          const demoBounceEvents: EmailEvent[] = DEMO_BOUNCE_EVENTS.map(event => ({
            id: event.id,
            event_type: 'EMAIL_BOUNCE' as const,
            from_email: event.senderEmail,
            to_email: event.leadEmail,
            to_name: event.leadName,
            campaign_name: event.campaignName,
            campaign_id: Math.floor(Math.random() * 10) + 1,
            client_id: Math.floor(Math.random() * 8) + 1,
            subject: event.subject,
            bounce_message: 'Mailbox does not exist',
            created_at: event.timestamp
          }));

          setSentEvents(demoSentEvents);
          setReplyEvents(demoReplyEvents);
          setBounceEvents(demoBounceEvents);
          setLoading(false);
          return;
        }

        console.log('🔍 [EmailsSentLiveStream] Fetching today\'s events using UTC date boundaries...')
        
        // Calculate today's date range in UTC to match PostgreSQL CURRENT_DATE behavior
        const now = new Date()
        const todayUTC = new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
        const tomorrowUTC = new Date(todayUTC.getTime() + 24 * 60 * 60 * 1000)
        
        console.log('📅 [EmailsSentLiveStream] UTC date range:', {
          start: todayUTC.toISOString(),
          end: tomorrowUTC.toISOString()
        })
        
        // Fetch sent events using UTC date filtering (limit 100)
        const { data: sentData, error: sentError } = await supabase
          .from('obsync_dynamic_reports')
          .select('*')
          .eq('event_type', 'EMAIL_SENT')
          .gte('created_at', todayUTC.toISOString())
          .lt('created_at', tomorrowUTC.toISOString())
          .order('created_at', { ascending: false })
          .limit(100)

        // Fetch reply events using UTC date filtering (limit 100)
        const { data: replyData, error: replyError } = await supabase
          .from('obsync_dynamic_reports')
          .select('*')
          .eq('event_type', 'EMAIL_REPLY')
          .gte('created_at', todayUTC.toISOString())
          .lt('created_at', tomorrowUTC.toISOString())
          .order('created_at', { ascending: false })
          .limit(100)

        // Fetch bounce events using UTC date filtering (limit 100)
        const { data: bounceData, error: bounceError } = await supabase
          .from('obsync_dynamic_reports')
          .select('*')
          .eq('event_type', 'EMAIL_BOUNCE')
          .gte('created_at', todayUTC.toISOString())
          .lt('created_at', tomorrowUTC.toISOString())
          .order('created_at', { ascending: false })
          .limit(100)

        if (sentError) console.error('❌ [EmailsSentLiveStream] Error fetching sent events:', sentError)
        if (replyError) console.error('❌ [EmailsSentLiveStream] Error fetching reply events:', replyError)
        if (bounceError) console.error('❌ [EmailsSentLiveStream] Error fetching bounce events:', bounceError)

        // Transform data
        const transformedSent = (sentData || []).map(event => ({
          id: event.id,
          event_type: 'EMAIL_SENT' as const,
          from_email: event.from_email || 'Unknown',
          to_email: event.to_email || 'Unknown',
          to_name: event.to_name,
          campaign_name: event.campaign_name,
          campaign_id: event.campaign_id,
          client_id: event.client_id,
          subject: event.subject,
          created_at: event.created_at,
          category: event.category
        }))

        const transformedReplies = (replyData || []).map(event => ({
          id: event.id,
          event_type: 'EMAIL_REPLY' as const,
          from_email: event.from_email || 'Unknown',
          to_email: event.to_email || 'Unknown',
          to_name: event.to_name,
          campaign_name: event.campaign_name,
          campaign_id: event.campaign_id,
          client_id: event.client_id,
          subject: event.subject,
          reply_body: event.reply_body,
          created_at: event.created_at,
          category: event.category
        }))

        const transformedBounces = (bounceData || []).map(event => ({
          id: event.id,
          event_type: 'EMAIL_BOUNCE' as const,
          from_email: event.from_email || 'Unknown',
          to_email: event.to_email || 'Unknown',
          to_name: event.to_name,
          campaign_name: event.campaign_name,
          campaign_id: event.campaign_id,
          client_id: event.client_id,
          subject: event.subject,
          bounce_message: event.bounce_message,
          created_at: event.created_at,
          category: event.category
        }))

        console.log(`📊 [EmailsSentLiveStream] Fetched today's events using SQL CURRENT_DATE - Sent: ${transformedSent.length}, Replies: ${transformedReplies.length}, Bounces: ${transformedBounces.length}`)

        setSentEvents(transformedSent)
        setReplyEvents(transformedReplies)
        setBounceEvents(transformedBounces)
        setLoading(false)
      } catch (error) {
        console.error('💥 [EmailsSentLiveStream] Error in fetchTodayEvents:', error)
        setLoading(false)
      }
    }

    // Clear any cached data and fetch fresh
    setSentEvents([])
    setReplyEvents([])
    setBounceEvents([])
    
    fetchTodayEvents()
    
    // Set up real-time subscription for live updates (only if not in demo mode)
    let channel: any = null;
    let interval: any = null;
    
    if (smartleadAPI.isConfigured()) {
      const channelName = `email-events-live-${Math.random().toString(36).substr(2, 9)}`;
      channel = supabase
        .channel(channelName)
        .on('postgres_changes', 
          { event: 'INSERT', schema: 'public', table: 'obsync_dynamic_reports' },
          (payload) => {
            console.log('🔴 [EmailsSentLiveStream] New email event received:', payload.new)
            const newEvent = payload.new as any
            
            // Only process email events from today
            if (['EMAIL_SENT', 'EMAIL_REPLY', 'EMAIL_BOUNCE'].includes(newEvent.event_type)) {
              // Refresh data to get updated live stream events
              console.log('🔄 [EmailsSentLiveStream] Refreshing data after new event...')
              setTimeout(() => fetchTodayEvents(), 500)
            }
          }
        )
        .subscribe()

      // Refresh data every 30 seconds as backup
      interval = setInterval(() => {
        console.log('🔄 [EmailsSentLiveStream] Periodic refresh triggered')
        fetchTodayEvents()
      }, 30000)
    }

    return () => {
      console.log('🧹 [EmailsSentLiveStream] Cleaning up subscription and interval');
      if (channel) {
        supabase.removeChannel(channel)
      }
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [emailAnalytics?.today, emailAnalytics?.today_replies, emailAnalytics?.today_bounces])

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMs = now.getTime() - date.getTime()
    const diffInSeconds = Math.floor(diffInMs / 1000)
    
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`
    
    const diffInMinutes = Math.floor(diffInSeconds / 60)
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours}h ago`
    
    const diffInDays = Math.floor(diffInHours / 24)
    return `${diffInDays}d ago`
  }

  const handleEventClick = async (eventId: string) => {
    try {
      // Fetch the full event data including the JSON data column
      const { data: eventData, error } = await supabase
        .from('obsync_dynamic_reports')
        .select('*')
        .eq('id', eventId)
        .single()

      if (error) {
        console.error('Error fetching event details:', error)
        return
      }

      if (eventData) {
        setSelectedEvent(eventData)
        setIsModalOpen(true)
      }
    } catch (error) {
      console.error('Error handling event click:', error)
    }
  }

  const renderEventItem = (event: EmailEvent, index: number) => (
    <div 
      key={event.id} 
      className={`flex items-start gap-3 p-3 rounded-lg transition-all duration-300 cursor-pointer hover:bg-muted/50 ${
        index === 0 ? 'bg-primary/10 border border-primary/20' : 'bg-muted/30'
      }`}
      onClick={() => handleEventClick(event.id)}
    >
      <div className="flex justify-between items-start w-full">
        <div className="flex-1 space-y-1">
          {/* Line 1: Lead Name + Lead Email */}
          <div className="flex items-center gap-2">
            {event.event_type === 'EMAIL_REPLY' && (
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            )}
            {event.event_type === 'EMAIL_SENT' && (
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            )}
            {event.event_type === 'EMAIL_BOUNCE' && (
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            )}
            <span className="font-medium text-sm">
              {event.to_name || event.to_email?.split('@')[0] || 'Unknown Lead'}
            </span>
            <span className="text-muted-foreground text-sm">•</span>
            <span className="text-muted-foreground text-sm truncate">
              {event.to_email || 'No email'}
            </span>
          </div>
          
          {/* Line 2: From Name + From Email */}
          <div className="text-xs text-muted-foreground">
            <span>From: </span>
            <span>{event.from_email?.split('@')[0] || 'Unknown'}</span>
            <span className="mx-1">&lt;</span>
            <span>{event.from_email || 'No email'}</span>
            <span>&gt;</span>
          </div>
          
          {/* Line 3: Campaign Name */}
          {event.campaign_name && (
            <div className="text-xs text-muted-foreground">
              <span>Campaign: </span>
              <span>{event.campaign_name}</span>
            </div>
          )}
          
          {/* Line 4: Subject */}
          {event.subject && (
            <div className="text-xs text-muted-foreground truncate">
              <span>Subject: </span>
              <span>{event.subject}</span>
            </div>
          )}
        </div>
        
        {/* Timestamp on the side */}
        <div className="text-xs text-muted-foreground flex-shrink-0 ml-3">
          {formatTimeAgo(event.created_at)}
        </div>
      </div>
    </div>
  )

  const renderEventStream = (events: EmailEvent[], title: string, icon: React.ReactNode, emptyMessage: string) => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      )
    }

    if (events.length === 0) {
      return (
        <div className="text-center py-8">
          {icon}
          <p className="text-muted-foreground text-sm mt-2">{emptyMessage}</p>
        </div>
      )
    }

    return (
      <div className="space-y-2">
        {events.map((event, index) => renderEventItem(event, index))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header with live indicator */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
            <h3 className="text-lg font-semibold">Real-Time Email Activity</h3>
          </div>
          <Badge variant="outline" className="text-xs">
            Live via Analytics
          </Badge>
        </div>
        
        <div className="flex items-center gap-6 text-sm">
          <div className="text-center">
            <div className="font-bold text-success flex items-center gap-1">
              <Send className="h-4 w-4" />
              {todayStats.total_sent.toLocaleString()}
            </div>
            <div className="text-xs text-muted-foreground">Sent Today</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-primary flex items-center gap-1">
              <MessageCircle className="h-4 w-4" />
              {todayStats.total_replies.toLocaleString()}
            </div>
            <div className="text-xs text-muted-foreground">Replies</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-destructive flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" />
              {todayStats.total_bounces.toLocaleString()}
            </div>
            <div className="text-xs text-muted-foreground">Bounces</div>
          </div>
        </div>
      </div>

      {/* Three Live Event Streams */}
      <div className="space-y-4">
        {/* Live Email Sent Events Stream */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Send className="h-4 w-4 text-success" />
              Live Email Sent Events Stream ({todayStats.total_sent.toLocaleString()})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[300px] p-4">
              {renderEventStream(
                sentEvents, 
                "Email Sent Events", 
                <Mail className="w-12 h-12 text-muted-foreground mx-auto" />,
                "No emails sent today yet"
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Live Email Reply Events Stream */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-primary" />
              Live Email Reply Events Stream ({todayStats.total_replies.toLocaleString()})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[300px] p-4">
              {renderEventStream(
                replyEvents, 
                "Email Reply Events", 
                <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto" />,
                "No replies received today yet"
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Live Email Bounce Events Stream */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              Live Email Bounce Events Stream ({todayStats.total_bounces.toLocaleString()})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[300px] p-4">
              {renderEventStream(
                bounceEvents, 
                "Email Bounce Events", 
                <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto" />,
                "No bounced emails today yet"
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Email Event Detail Modal */}
      <EmailEventDetailModal
        event={selectedEvent}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
      />
    </div>
  )
}
